import { nextLink, readJson, type FetchLike } from "@emdur/ui";
import { config, request, SignInRequired, type Transport } from "./api";
import type { Uploader } from "./apiContext";
import { startSignIn } from "./auth";
import { xhrTransport } from "./xhr";
import type { Patch } from "./mergePatch";

/** Where to come back to after signing in again: the page the person is on. */
const here = () => `${location.pathname}${location.search}`;

async function signed(
  path: string,
  init: RequestInit,
  transport?: Transport,
): Promise<Response> {
  try {
    const response = await request(
      init.method ?? "GET",
      path,
      { headers: init.headers, body: init.body },
      transport,
    );
    // The kit sees relative API paths. Resolve Link against the real API origin first, using its
    // existing RFC 8288 parser, then keep the next request relative (never another host).
    const link = response.headers.get("Link");
    const apiUrl = (await config()).api_url;
    const next = link ? nextLink(link, `${apiUrl}${path}`) : null;
    if (next) {
      const url = new URL(next);
      const headers = new Headers(response.headers);
      headers.set("Link", `<${url.pathname}${url.search}>; rel="next"`);
      return new Response(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers,
      });
    }
    return response;
  } catch (error) {
    // A lapsed session goes back to sign-in; the promise never settles, since the page is leaving.
    if (error instanceof SignInRequired) return startSignIn(here());
    throw error;
  }
}

/** The kit's fetcher (readJson, usePaged, the editor's saves): a path on the API, signed with the person's token. */
export const apiFetch: FetchLike = (input, init = {}) => signed(input, init);

/** Public operations (listFontFamilies): no token. */
export const publicFetch: FetchLike = async (input, init) =>
  fetch(`${(await config()).api_url}${input}`, init);

/** Exhaust a contracted cursor collection through the kit's Link parser; never follow a repeated page. */
export async function readAll<T>(
  fetcher: FetchLike,
  path: string,
): Promise<T[]> {
  const items: T[] = [];
  const seen = new Set<string>();
  let next: string | null = path;
  while (next) {
    if (seen.has(next))
      throw new Error("The list repeated a page. Reload to try again.");
    seen.add(next);
    const answer: { data: { items: T[] }; next: string | null } =
      await readJson<{ items: T[] }>(fetcher, next);
    items.push(...answer.data.items);
    next = answer.next;
  }
  return items;
}
/** Registry resources need a server version before a conditional merge patch. */
export async function readVersioned<T>(
  fetcher: FetchLike,
  path: string,
  subject: string,
) {
  const answer = await readJson<T>(fetcher, path);
  if (!answer.etag)
    throw new Error(
      `The ${subject}'s version could not be read. Reload before changing it.`,
    );
  return { ...answer, etag: answer.etag };
}
/** The same conditional RFC 7396 write for apps, roles and agents. */
export const patchVersioned = <T>(
  fetcher: FetchLike,
  path: string,
  patch: Patch,
  etag: string,
  key: string,
) =>
  readJson<T>(fetcher, path, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/merge-patch+json",
      "If-Match": etag,
      "Idempotency-Key": key,
    },
    body: JSON.stringify(patch),
  });

/** uploadFile: the file's bytes as the body, with upload progress. */
export const upload: Uploader = (path, file, key, onProgress) =>
  signed(
    path,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "Idempotency-Key": key,
      },
      body: file,
    },
    xhrTransport(onProgress),
  );
